import RouterService from '@ember/routing/router-service';
import type Transition from '@ember/routing/transition';
import Service, { service } from '@ember/service';
import { unprotectedRoutes } from 'scaleflow/routes/application';
import type BetaToggleService from 'scaleflow/services/beta-toggle';

export default class FeatureAccessService extends Service {
  @service currentUser: any;
  @service declare router: RouterService;
  @service declare betaToggle: BetaToggleService;

  setupWasCalled = false;

  setup() {
    this.setupWasCalled = true;
    this.router.on('routeWillChange', this.checkIfUserIsPermittedToVisitRoute);
    this.router.on('routeDidChange', this.checkIfUserIsPermittedToVisitRoute);
  }

  get isAdmin(): boolean {
    const permissions: string[] = this.userPermissions;
    return permissions.some((permission) => permission.includes('/admin'));
  }

  get isPlatformAdmin(): boolean {
    return this.userPermissions.includes('platform-admins/admin');
  }

  get userPermissions(): string[] {
    return this.currentUser.permissions;
  }

  get hasAccessToEverything(): boolean {
    if (this.permissionsMap.has('scans')) return false;
    return this.isAdmin;
  }

  setScanFeaturesToTrue(map: Map<string, boolean>) {
    map.set('scans', true);
    map.set('connectors', true);
    map.set('rfi', true);
  }

  get permissionsMap() {
    const map = new Map<string, boolean>();
    this.userPermissions.forEach((permission) => {
      const scope = permission.split('/')[1];
      map.set(scope!.toLowerCase(), true);
    });
    if (this.betaToggle.isXrayCustomer && !this.betaToggle.isSaasCustomer) {
      this.setScanFeaturesToTrue(map);
    }
    // Knowledge base access is granted by its feature flag rather than by a
    // permission scope, so seed the key here. Without it this guard redirects
    // `data.*` away before the route's own flag check ever gets a say.
    if (this.betaToggle.showKnowledgeBaseFeatures) map.set('data', true);

    return map;
  }

  checkIfUserIsPermittedToVisitRoute = (transition: Transition): void => {
    const routeName = transition?.to?.name;
    if (!routeName) return;
    if (this.hasAccessToEverything || unprotectedRoutes.includes(routeName))
      return;

    let isAllowed = false;
    const permissionsMap = this.permissionsMap;
    const keys = Array.from(permissionsMap.keys());
    isAllowed =
      routeName === 'loading' ||
      routeName === 'home' ||
      unprotectedRoutes.includes(routeName) ||
      keys.some((key: string) => routeName.includes(key));
    if (isAllowed) return;
    if (permissionsMap.has('ideas'))
      this.router.transitionTo('backlog.ideas.list');
    else if (permissionsMap.has('scans'))
      this.router.transitionTo('scans.list');
    else this.router.transitionTo('home');
  };

  canAccessFeature(feature: string): boolean {
    if (this.hasAccessToEverything) return true;
    const permissions: string[] = this.userPermissions;
    return permissions.some((permission) => permission.includes(feature));
  }

  willDestroy(): void {
    super.willDestroy();
    if (!this.setupWasCalled) return;
    this.router.off('routeDidChange', this.checkIfUserIsPermittedToVisitRoute);
    this.router.off('routeWillChange', this.checkIfUserIsPermittedToVisitRoute);
  }
}
